import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Ourteam.css';
import team1 from "../../assets/team/team1.png";
import team2 from "../../assets/team/team2.png";
import team3 from "../../assets/team/team3.png";
import team4 from "../../assets/team/team4.png";
import ContactUs from '../../Pages/ContactUs';

// ─────────────────────────────────────────────────────────────────────────────
// TEAM DATA — Add or update members here easily.
//
// Each member object supports:
//   name        : string  (required)
//   designation : string  (required)
//   experience  : string  (optional) — shown as small note below divider
//   photo       : string  (optional) — path/URL to image; NOT shown right now
//                 (photo display is hidden until real photos are ready —
//                 see SHOW_PHOTOS flag below)
//
// Example to add a new member:
//   {
//     name: 'John Smith',
//     designation: 'Project Engineer',
//     experience: '8 years in geotechnical works',
//     photo: '/images/team/john.jpg',
//   },
// ─────────────────────────────────────────────────────────────────────────────

// Set this to true later, once real team photos are ready, to bring the
// photo area back without touching any other part of this file.
const SHOW_PHOTOS = false;

const TEAM_MEMBERS = [
  {
    name: 'K. Nedunchezian',
    designation: 'Director & Operations Head',
    experience: '25+ years in Construction & Geotechnical field',
    photo: team1,
  },
  {
    name: 'M. Subenthiran',
    designation: 'General Manager',
    experience: '25+ years in Construction & Ground Improvement',
    photo: team2,
  },
  {
    name: 'Killiyappan Gopal',
    designation: 'Major Shareholder & Director',
    experience: '30+ years experience director of JAK Engineering & Jae Hwan Construction.',
    photo: team3,
  },

  // ── Add more team members below this line ────────────────────────────────
];

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — Tune carousel behaviour here
// ─────────────────────────────────────────────────────────────────────────────
const AUTO_SLIDE_INTERVAL = 3500;  // ms between auto-slides
const CARDS_PER_VIEW_LG   = 3;     // ≥1025px
const CARDS_PER_VIEW_MD   = 3;     // 769–1024px
const CARDS_PER_VIEW_SM   = 2;     // 521–768px
const CARDS_PER_VIEW_XS   = 1;     // ≤520px
const SWIPE_THRESHOLD_PX  = 40;    // minimum finger drag distance to trigger a slide change

// ─────────────────────────────────────────────────────────────────────────────
// Helper — derive initials from name
// ─────────────────────────────────────────────────────────────────────────────
const getInitials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

// ─────────────────────────────────────────────────────────────────────────────
// Hook — responsive cards per view
// ─────────────────────────────────────────────────────────────────────────────
const useCardsPerView = () => {
  const getCount = () => {
    const w = window.innerWidth;
    if (w > 1024) return CARDS_PER_VIEW_LG;
    if (w > 768)  return CARDS_PER_VIEW_MD;
    if (w > 520)  return CARDS_PER_VIEW_SM;
    return CARDS_PER_VIEW_XS;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handler = () => setCount(getCount());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return count;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const OurTeam = () => {
  const cardsPerView   = useCardsPerView();
  const totalMembers   = TEAM_MEMBERS.length;
  const maxIndex       = Math.max(0, totalMembers - cardsPerView);
  const totalDots      = maxIndex + 1;

  const [current, setCurrent]       = useState(0);
  const [isPaused, setIsPaused]     = useState(false);
  const trackRef                    = useRef(null);
  const timerRef                    = useRef(null);

  // ── touch / swipe tracking ───────────────────────────────────────────────
  const touchStartX = useRef(null);
  const touchDeltaX  = useRef(0);

  // ── translate track ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!trackRef.current) return;
    const cardEl   = trackRef.current.children[0];
    if (!cardEl)   return;
    const cardW    = cardEl.getBoundingClientRect().width;
    const gap      = 24;
    const offset   = current * (cardW + gap);
    trackRef.current.style.transform = `translateX(-${offset}px)`;
  }, [current, cardsPerView]);

  // ── auto slide ───────────────────────────────────────────────────────────
  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, AUTO_SLIDE_INTERVAL);
  }, [maxIndex]);

  useEffect(() => {
    if (!isPaused) startTimer();
    return () => clearInterval(timerRef.current);
  }, [isPaused, startTimer]);

  // ── navigation ───────────────────────────────────────────────────────────
  const goTo = (index) => {
    const clamped = Math.max(0, Math.min(index, maxIndex));
    setCurrent(clamped);
    startTimer(); // reset timer on manual nav
  };

  // NOTE: goPrev/goNext are kept (used by dot pagination + keyboard arrows),
  // only the clickable prev/next ARROW BUTTONS have been removed from the UI.
  // Both wrap around — this is what makes the carousel loop infinitely.
  const goPrev = () => goTo(current > 0 ? current - 1 : maxIndex);
  const goNext = () => goTo(current < maxIndex ? current + 1 : 0);

  // ── keyboard nav ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, maxIndex]);

  // ── touch / swipe handlers (mobile finger drag) ──────────────────────────
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
    setIsPaused(true); // pause auto-slide while the user is interacting
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (touchDeltaX.current > SWIPE_THRESHOLD_PX) {
      goPrev(); // swiped right → previous member
    } else if (touchDeltaX.current < -SWIPE_THRESHOLD_PX) {
      goNext(); // swiped left → next member
    }
    touchStartX.current = null;
    touchDeltaX.current = 0;
    setIsPaused(false); // resume auto-slide
  };

  return (
    <section
      className="team-section"
      id="team"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* decorative glow */}
      <div className="team-glow-left" />

      <div className="team-container">

        {/* ── Header ── */}
        <div className="team-header">
          <div className="team-header-left">
            <div className="team-eyebrow">
              <span className="team-eyebrow-line" />
              Our Team
              <span className="team-eyebrow-line" />
            </div>
            <h2 className="team-title">
              The People Behind<br />
              <span>GSK Geotech</span>
            </h2>
            <p className="team-subtitle">
              Experienced professionals who bring decades of geotechnical
              expertise to every project across Singapore.
            </p>
          </div>

          {/* Prev / Next arrow buttons removed — navigation is via dots below */}
        </div>

        {/* ── Carousel ── */}
        <div
          className="team-carousel-wrapper"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="team-track" ref={trackRef}>
            {TEAM_MEMBERS.map((member) => (
              <div className="team-card" key={member.name}>

                {/* Photo area — hidden until real photos are ready.
                    Flip SHOW_PHOTOS to true above to bring it back. */}
                {SHOW_PHOTOS && (
                  <div className="team-card-photo">
                    {member.photo ? (
                      <img src={member.photo} alt={member.name} />
                    ) : (
                      <div className="team-card-photo-placeholder">
                        <span className="team-card-photo-placeholder-initials">
                          {getInitials(member.name)}
                        </span>
                        <span className="team-card-photo-placeholder-label">
                          Photo Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Info */}
                <div className="team-card-info">
                  <div className="team-card-avatar">
                    <span>{getInitials(member.name)}</span>
                  </div>
                  <div className="team-card-name">{member.name}</div>
                  <div className="team-card-designation">{member.designation}</div>
                  <div className="team-card-divider" />
                  {member.experience && (
                    <div className="team-card-exp">{member.experience}</div>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* ── Dot Pagination ── */}
        {totalDots > 1 && (
          <div className="team-dots">
            {Array.from({ length: totalDots }).map((_, i) => (
              <button
                key={i}
                className={`team-dot${current === i ? ' active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        )}

      </div>
    </section>
  );
};

export default OurTeam;